import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod/v4";

import { getFormerCarsQueryOptions } from "./get-former-cars";
import { getGarageCarsQueryOptions } from "./get-garage-cars";

import { api } from "@/lib/api-client";

import type { MutationConfig } from "@/lib/react-query";

export const deleteCarInputSchema = z.object({
  exit_price: z.number().min(0.01),
  status: z.enum(["sold", "traded", "totaled", "other"]),
});

export type DeleteCarInput = z.infer<typeof deleteCarInputSchema>;

export const deleteCar = async ({
  data,
  carId,
}: {
  carId: string;
  data: DeleteCarInput;
  garageId: string;
}) => {
  return await api.put(`/carburate/cars/${carId}/remove/`, data);
};

type UseDeleteCarOptions = {
  mutationConfig?: MutationConfig<typeof deleteCar>;
};

export const useDeleteCar = ({ mutationConfig }: UseDeleteCarOptions = {}) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (data, { garageId, ...args }, ...rest) => {
      queryClient.invalidateQueries({
        queryKey: getGarageCarsQueryOptions({ garageId, page: 1, pageSize: 999999 }).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: ["cars"] });
      queryClient.invalidateQueries({
        queryKey: getFormerCarsQueryOptions().queryKey,
      });
      queryClient.invalidateQueries({ queryKey: ["garage-market-car-ids"] });
      // Refresh portfolio totals after removing/moving a car
      queryClient.invalidateQueries({ queryKey: ["portfolio"] });
      onSuccess?.(data, { garageId, ...args }, ...rest);
    },
    ...restConfig,
    mutationFn: deleteCar,
  });
};
